
import { computed, defineComponent, onMounted, onUnmounted, ref, useStore } from '@nuxtjs/composition-api'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import { toDate } from '~/assets/util'
import { setupTixPricing, setupTixSale } from '~/assets/composition/tix'
import { setupConcertStatus } from '~/assets/i-concert'
import { createMusicEventFromPage } from '~/assets/structured-data/music-event'
import { setupGtmCdpAction, setupGtmCdpConcertStatus, setupGtmCdpEvent, setupGtmClickOrderTickets, setupGtmViewItem } from '~/assets/composition/setup-gtm'
import { dateInPast } from '@/assets/date'
import { setupHotjar } from '@/assets/composition/setup-hotjar'

export default defineComponent({
  props: {
    breadcrumbs: { type: Array, required: true },
    page: { type: Object, required: true },
  },
  setup({ page }) {
    // track the page view
    setupGtmViewItem(page)
    setupGtmCdpEvent('CV07 - V2')
    setupHotjar('CV07_V2')

    const store = useStore()
    const gtmCdpAction = setupGtmCdpAction()

    // favorites
    const favoriteEventIConcertIds = computed(() => store.getters['favorites/eventIConcertIds'])
    const isFavorite = computed(() => favoriteEventIConcertIds.value.includes(page.iConcertId))

    const favoritesToggleEventId = () => {
      if (!isFavorite.value) {
        gtmCdpAction('CV07 - Save this concert clicked')
      }
      store.commit('favorites/toggleEventIConcertId', page.iConcertId)
    }
    const favoritesOpenOverlay = (eventIConcertId) => {
      store.commit('favorites/openOverlayAndStoreId', eventIConcertId)
    }

    const { hasVriendenloterijTicketType, minPrice } = setupTixPricing(page.pricing, page.eventCustomerTags)
    const tixSale = setupTixSale(page)
    const concertStatus = setupConcertStatus(page.concertStatus, 'event-detail')
    // TODO: Create setup function and possible merge with setupConcertStatus
    const concertState = computed(() => ({
      EXPIRED: dateInPast(toDate(page.eventEndDate) || toDate(page.eventDate), new Date()),
      SOLD_OUT: tixSale.saleTemplate.value === 'tix-sale-status-sold-out',
      MOVED: page.concertStatus === 'Verplaatst',
      CANCELED: page.concertStatus === 'Geannuleerd',
    }))
    const hasLastTicketsStatus = tixSale?.saleTemplate.value === 'tix-sale-status-few-tickets'

    setupGtmCdpConcertStatus(concertState.value, hasLastTicketsStatus)

    // Audio
    const headerAudioIsPlaying = ref(false)

    const toggleHeaderAudio = (isPlaying) => {
      headerAudioIsPlaying.value = isPlaying
    }

    // Sticky element
    const func = debounce(() => {
      const mobileStatic = document.querySelector('.event-detail-info__data-mobile--static')
      const mobileSticky = document.querySelector('.event-detail-info__data-mobile--sticky')
      const mobileDisplay = window.getComputedStyle(mobileStatic).display

      if (mobileDisplay !== 'none') {
        const mobileStaticPos = mobileStatic.getBoundingClientRect()

        if (mobileStaticPos.bottom < 0) {
          mobileSticky.classList.remove('hidden')
          mobileSticky.classList.add('visible')
        } else {
          mobileSticky.classList.remove('visible')
          mobileSticky.classList.add('hidden')
        }
      } else {
        const element = document.querySelector('.sticky-element-desktop')
        const elementPos = element.getBoundingClientRect()
        const parent = element.parentElement.getBoundingClientRect()

        if (parent.top < elementPos.top) {
          element.classList.add('i-is-stuck-on-desktop')
        } else {
          element.classList.remove('i-is-stuck-on-desktop')
        }
      }
    }, 25)

    // Make sure that the event listener is also removed, otherwise they will accumulate
    onMounted(() => window.addEventListener('scroll', func))
    onUnmounted(() => window.removeEventListener('scroll', func))

    // Additional events
    // Compute the additionalEvents, i.e. events in the production except the current one
    const additionalEvents = computed(() => page.production?.events?.filter((event) => event.url !== page.url))

    return {
      additionalEvents,
      headerAudioIsPlaying,
      toggleHeaderAudio,
      isFavorite,
      favoritesEnabled: computed(() => store.getters['favorites/enabled']),
      favoritesToggleEventId,
      favoritesOpenOverlay,
      get,
      hasVriendenloterijTicketType,
      jsonld: createMusicEventFromPage(page, minPrice, tixSale),
      minPrice,
      orderClick: setupGtmClickOrderTickets(page),
      theme: page.production?.theme,
      tixSale,
      toDate,
      concertStatus,
      concertState,
    }
  },
  jsonld() {
    return this.jsonld
  },
})
